import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { matte, colored, vary, seededRandom, jitter, Fader } from '../../Style.js';
import { SEA, hashId, foamFrame, foamRing, foamMesh, palmParts, rockGeometry, ropeParts } from './style.js';

// Beacon lamps are shared by every pylon and blink together. They are light,
// not surface, so they never fade.
const LAMP_MATERIAL = new THREE.MeshBasicMaterial({ color: SEA.lamp, toneMapped: false });

// Obstacles keep the exact footprint the server collides against, dressed as
// breakwaters, sea stacks and beacons, floating cargo and wrecks, and rocky
// islands reached by timber jetties.
export class Obstacle {
  constructor(scene, obstacleData) {
    this.scene = scene;
    this.data = obstacleData;
    this.mesh = null;
    this.fader = null;
    this.floaters = [];
    this.lamps = [];
    this.rand = seededRandom(hashId(obstacleData.id));
    this.phase = this.rand() * Math.PI * 2;
    this.create();
  }

  create() {
    const { type, position, size, rotation } = this.data;

    this.mesh = new THREE.Group();
    // Anything standing at sea level gets surf around it
    this.inWater = (position.y || 0) < 0.5;

    switch (type) {
      case 'wall':
        this.buildWall(size);
        break;
      case 'pillar':
        this.buildPillar(size);
        break;
      case 'cover':
        this.buildCover(size);
        break;
      case 'ramp':
        // Zero elevations are omitted from the server's JSON
        this.buildRamp(size, this.data.elevationStart ?? 0, this.data.elevationEnd ?? 0);
        break;
      default:
        this.addMerged([colored(new THREE.BoxGeometry(size.x, size.y, size.z), SEA.stone, { y: size.y / 2 })]);
    }

    // Ramps are built from their footprint origin; everything else from its base
    this.mesh.position.set(position.x, position.y, position.z);

    if (rotation) {
      this.mesh.rotation.y = rotation;
    }

    this.scene.add(this.mesh);
    this.mesh.updateMatrixWorld(true);

    // Ramps are walked on, so they never fade out of the way
    if (type !== 'ramp') {
      this.fader = new Fader(this.mesh);
    }
  }

  addMerged(geometries, parent = this.mesh) {
    const mesh = new THREE.Mesh(mergeGeometries(geometries), matte(0xffffff, { vertexColors: true }));
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    parent.add(mesh);
    return mesh;
  }

  addFoam(geometry) {
    if (this.inWater) this.mesh.add(foamMesh(geometry));
  }

  // Breakwater: sea-worn blocks with a weed-and-barnacle tide line, a coping
  // course, and rubble heaped along the foot.
  buildWall(size) {
    const rand = this.rand;
    const alongX = size.x >= size.z;
    const length = alongX ? size.x : size.z;
    const thickness = alongX ? size.z : size.x;
    const height = size.y;
    const ry = alongX ? 0 : Math.PI / 2;
    const parts = [];
    const add = (geo, color, x, y, z) => parts.push(colored(geo, color, { x, y, z }).rotateY(ry));

    const top = height - 0.5;
    const courses = Math.max(2, Math.round(top / 0.85));
    const h = top / courses;
    for (let row = 0; row < courses; row++) {
      let cursor = -length / 2;
      let first = row % 2 ? 0.6 + rand() * 0.6 : 1.1 + rand() * 0.8;
      while (cursor < length / 2 - 0.01) {
        const block = Math.min(first, length / 2 - cursor);
        first = 1.1 + rand() * 1.3;
        const color = row === 0 ? vary(SEA.stoneDark, rand, 0.1) : vary(SEA.stone, rand, 0.14, 0.02);
        const geo = jitter(new THREE.BoxGeometry(Math.max(0.05, block - 0.07), h - 0.06, thickness - rand() * 0.12), 0.06, rand);
        add(geo, color, cursor + block / 2, row * h + h / 2, 0);
        cursor += block;
      }
    }
    add(new THREE.BoxGeometry(length - 0.04, top, thickness * 0.8), SEA.stoneDark, 0, top / 2, 0);

    // Coping slabs
    let cursor = -length / 2;
    while (cursor < length / 2 - 0.01) {
      const slab = Math.min(1.8 + rand(), length / 2 - cursor);
      add(new THREE.BoxGeometry(slab - 0.05, 0.5, thickness + 0.3), vary(SEA.rockLight, rand, 0.08), cursor + slab / 2, top + 0.25, 0);
      cursor += slab;
    }

    // Tide line on both faces, and rubble rip-rap at the foot
    for (const side of [-1, 1]) {
      const face = side * (thickness / 2 + 0.05);
      for (let x = -length / 2 + 0.6; x < length / 2 - 0.6; x += 0.9 + rand() * 0.9) {
        add(new THREE.BoxGeometry(0.7 + rand() * 0.7, 0.3 + rand() * 0.5, 0.1), vary(SEA.seaweed, rand, 0.12), x, 0.3, face);
        if (rand() < 0.7) add(new THREE.BoxGeometry(0.13, 0.13, 0.1), SEA.barnacle, x + rand() * 0.5, 0.75 + rand() * 0.6, face + side * 0.04);
      }
      for (let x = -length / 2; x < length / 2; x += 1.6 + rand() * 1.6) {
        const r = 0.45 + rand() * 0.5;
        parts.push(rockGeometry(rand, r, vary(SEA.rockDark, rand, 0.1), { x: 0, y: 0, z: 0 })
          .translate(x, r * 0.15, side * (thickness / 2 + r * 0.5)).rotateY(ry));
      }
    }
    this.addMerged(parts);
    this.addFoam(foamFrame(alongX ? length : thickness + 1.6, alongX ? thickness + 1.6 : length, 1.6));
  }

  // A sea stack of piled rock topped with grass, or now and then a little
  // beacon tower on a rock base.
  buildPillar(size) {
    const rand = this.rand;
    const radius = size.x / 2;
    const height = size.y;
    const parts = [];

    if (rand() < 0.35) {
      // Beacon: rocks, a white tower with a red cap and a lamp
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        parts.push(rockGeometry(rand, radius * 0.7, vary(SEA.rock, rand, 0.1), { x: Math.cos(a) * radius * 0.7, y: 0.2, z: Math.sin(a) * radius * 0.7, squash: 0.6 }));
      }
      parts.push(colored(new THREE.CylinderGeometry(radius * 0.55, radius * 0.8, height - 1.6, 8), SEA.white, { y: (height - 1.6) / 2 + 0.3 }));
      parts.push(colored(new THREE.CylinderGeometry(radius * 0.62, radius * 0.75, 0.6, 8), SEA.red, { y: height * 0.45 }));
      parts.push(colored(new THREE.CylinderGeometry(radius * 0.75, radius * 0.75, 0.15, 8), SEA.iron, { y: height - 1.2 }));
      parts.push(colored(new THREE.ConeGeometry(radius * 0.65, 0.8, 8), SEA.red, { y: height - 0.1 }));
      this.addMerged(parts);
      const lamp = new THREE.Mesh(new THREE.CylinderGeometry(radius * 0.4, radius * 0.4, 0.7, 8), LAMP_MATERIAL);
      lamp.position.y = height - 0.8;
      lamp.userData.noFade = true;
      this.mesh.add(lamp);
      this.lamps.push(lamp);
    } else {
      // Sea stack: tapering rock drums, each nudged off-centre, with a grassy cap
      const drums = 4;
      let y = 0;
      for (let i = 0; i < drums; i++) {
        const t = i / drums;
        const h = height / drums;
        const r = radius * (1.15 - t * 0.35);
        const drum = jitter(new THREE.CylinderGeometry(r * 0.9, r, h + 0.2, 7, 1), r * 0.18, rand);
        parts.push(colored(drum, vary(i === 0 ? SEA.rockDark : SEA.rock, rand, 0.1), { x: (rand() - 0.5) * 0.3, y: y + h / 2, z: (rand() - 0.5) * 0.3, ry: rand() * 3 }));
        y += h;
      }
      parts.push(colored(jitter(new THREE.CylinderGeometry(radius * 0.85, radius * 0.75, 0.4, 7), 0.12, rand), SEA.grass, { y: height + 0.1 }));
      parts.push(colored(new THREE.BoxGeometry(radius * 1.4, 0.3, 0.12), SEA.barnacle, { y: 0.9, z: radius * 1.05, ry: rand() }));
      parts.push(colored(new THREE.BoxGeometry(radius * 1.8, 0.5, radius * 1.8), SEA.seaweed, { y: 0.25 }));
      // A gull's nest of a tuft on top
      parts.push(colored(new THREE.ConeGeometry(0.3, 0.7, 4), SEA.grassDark, { x: 0.3, y: height + 0.6, z: -0.2 }));
      this.addMerged(parts);
    }
    this.addFoam(foamRing(radius * 1.05, 1.5));
  }

  // On the water: crates and barrels lashed to a float, or a half-sunk wreck
  // for long thin cover. Up on an island: a timber and sandbag parapet.
  buildCover(size) {
    const longest = Math.max(size.x, size.z);
    const shortest = Math.min(size.x, size.z);
    if (!this.inWater) {
      this.buildParapet(size);
    } else if (longest / shortest > 3) {
      this.buildWreck(size);
    } else {
      this.buildCargo(size);
    }
  }

  buildCargo(size) {
    const rand = this.rand;
    const parts = [];
    // Low raft the cargo floats on
    const raftHeight = 0.35;
    const planks = Math.max(3, Math.round(size.x / 0.6));
    for (let i = 0; i < planks; i++) {
      const x = -size.x / 2 + (size.x / planks) * (i + 0.5);
      parts.push(colored(new THREE.BoxGeometry(size.x / planks - 0.05, raftHeight, size.z + 0.3), vary(SEA.driftwood, rand, 0.12), { x, y: raftHeight / 2 }));
    }
    const crate = (w, h, d, x, y, z, ry) => {
      parts.push(colored(new THREE.BoxGeometry(w, h, d), vary(SEA.woodLight, rand, 0.12), { x, y: y + h / 2, z, ry }));
      parts.push(colored(new THREE.BoxGeometry(w + 0.04, 0.14, d + 0.04), SEA.woodDark, { x, y: y + 0.1, z, ry }));
      parts.push(colored(new THREE.BoxGeometry(w + 0.04, 0.14, d + 0.04), SEA.woodDark, { x, y: y + h - 0.1, z, ry }));
      parts.push(colored(new THREE.BoxGeometry(w * 0.14, h, d + 0.05), SEA.woodDark, { x, y: y + h / 2, z, ry }));
    };
    const top = size.y;
    const lower = (top - raftHeight) * 0.6;
    crate(size.x * 0.62, lower, size.z * 0.62, -size.x * 0.17, raftHeight, -size.z * 0.17, 0);
    crate(size.x * 0.42, top - raftHeight - lower, size.z * 0.42, -size.x * 0.2, raftHeight + lower, -size.z * 0.15, (rand() - 0.5) * 0.5);
    // Barrels: iron-hooped
    for (const [bx, bz] of [[size.x * 0.28, size.z * 0.22], [size.x * 0.28, -size.z * 0.25], [-size.x * 0.2, size.z * 0.3]]) {
      const r = Math.min(size.x, size.z) * 0.15;
      const h = lower * 0.9;
      parts.push(colored(new THREE.CylinderGeometry(r * 0.9, r * 0.9, h, 8), vary(SEA.wood, rand, 0.1), { x: bx, y: raftHeight + h / 2, z: bz }));
      for (const hy of [0.2, 0.8]) parts.push(colored(new THREE.CylinderGeometry(r * 0.95, r * 0.95, 0.06, 8), SEA.iron, { x: bx, y: raftHeight + h * hy, z: bz }));
    }
    // Lashing ropes over the stack
    parts.push(...ropeParts(new THREE.Vector3(-size.x / 2, raftHeight, 0.3), new THREE.Vector3(size.x / 2, raftHeight, 0.3), { sag: -lower * 1.1, radius: 0.04, segments: 6 }));

    const group = new THREE.Group();
    this.addMerged(parts, group);
    this.mesh.add(group);
    this.floaters.push(group);
    this.addFoam(foamFrame(size.x + 0.3, size.z + 0.3, 1.2));
  }

  // The bow of a wrecked ship nosing up out of the water, ribs showing.
  buildWreck(size) {
    const rand = this.rand;
    const alongX = size.x >= size.z;
    const length = alongX ? size.x : size.z;
    const width = alongX ? size.z : size.x;
    const height = size.y;
    const ry = alongX ? Math.PI / 2 : 0;
    const parts = [];
    const ribs = Math.max(4, Math.round(length / 0.9));
    for (let i = 0; i < ribs; i++) {
      const t = i / (ribs - 1);
      const z = -length / 2 + t * length;
      // Rises towards the bow, the stern lost below water
      const h = height * (0.45 + t * 0.55);
      for (const side of [-1, 1]) {
        parts.push(colored(new THREE.BoxGeometry(0.16, h, 0.2), vary(SEA.woodDark, rand, 0.1), { x: side * width * 0.42, y: h / 2, z, rz: side * 0.18 }));
      }
      // Planking survives on the lower half
      if (rand() < 0.75) {
        for (const side of [-1, 1]) {
          parts.push(colored(new THREE.BoxGeometry(0.08, h * (0.5 + rand() * 0.4), length / ribs + 0.05), vary(SEA.wood, rand, 0.12), { x: side * width * 0.47, y: h * 0.3, z, rz: side * 0.18 }));
        }
      }
    }
    parts.push(colored(new THREE.BoxGeometry(width * 0.7, 0.2, length), vary(SEA.woodDark, rand, 0.08), { y: 0.25 }));
    // Keel and a snapped mast
    parts.push(colored(new THREE.BoxGeometry(0.2, height * 0.9, 0.25), SEA.woodDark, { y: height * 0.45, z: length / 2 }));
    parts.push(colored(new THREE.CylinderGeometry(0.12, 0.16, height * 1.4, 6), SEA.woodDark, { y: height * 0.6, z: length * 0.1, rx: 0.4, rz: 0.2 }));
    parts.push(colored(new THREE.BoxGeometry(width * 0.9, 0.3, length * 0.15), SEA.seaweed, { y: 0.15, z: -length * 0.3 }));
    this.addMerged(parts.map(g => g.rotateY(ry)));
    this.addFoam(foamFrame(alongX ? length : width, alongX ? width : length, 1.3));
  }

  // Raised barricade on an island: a timber fence backed by sandbags.
  buildParapet(size) {
    const rand = this.rand;
    const alongX = size.x >= size.z;
    const length = alongX ? size.x : size.z;
    const thickness = alongX ? size.z : size.x;
    const height = size.y;
    const ry = alongX ? 0 : Math.PI / 2;
    const parts = [];
    const add = (geo, color, opts) => parts.push(colored(geo, color, opts).rotateY(ry));

    const bags = Math.max(2, Math.round(length / 0.8));
    for (let row = 0; row < 2; row++) {
      for (let i = 0; i < bags - row; i++) {
        const x = -length / 2 + (length / bags) * (i + 0.5 + row * 0.5);
        add(jitter(new THREE.BoxGeometry(length / bags - 0.05, 0.38, thickness * 0.8), 0.05, rand), vary(0xc9b48a, rand, 0.1), { x, y: 0.2 + row * 0.38 });
      }
    }
    const posts = Math.max(2, Math.round(length / 1.6));
    for (let i = 0; i <= posts; i++) {
      const x = -length / 2 + (length / posts) * i;
      add(new THREE.CylinderGeometry(0.1, 0.12, height + 0.3, 5), SEA.woodDark, { x, y: (height + 0.3) / 2, z: thickness * 0.35 });
    }
    for (const y of [height * 0.6, height - 0.1]) {
      add(new THREE.BoxGeometry(length, 0.16, 0.1), vary(SEA.wood, rand, 0.1), { y, z: thickness * 0.38 });
    }
    this.addMerged(parts);
  }

  // A raised platform becomes a rocky island with a grassy top; a slope becomes
  // a timber jetty on stone footings. The walking surface stays at the
  // server's heights.
  buildRamp(size, elevStart, elevEnd) {
    const rise = elevEnd - elevStart;
    if (Math.abs(rise) < 0.01) {
      this.buildIsland(size, elevStart);
    } else {
      this.buildJetty(size, elevStart, elevEnd);
    }
  }

  buildIsland(size, elev) {
    const rand = this.rand;
    const hx = size.x / 2;
    const hz = size.z / 2;

    // Cliff body: a box whose side vertices are pushed out and jittered into rock
    const segX = Math.max(4, Math.round(size.x / 1.6));
    const segZ = Math.max(4, Math.round(size.z / 1.6));
    const body = new THREE.BoxGeometry(size.x, elev + 1.2, size.z, segX, 3, segZ).toNonIndexed();
    body.translate(0, (elev + 1.2) / 2 - 1.2, 0);
    const p = body.attributes.position;
    const offsets = new Map();
    for (let i = 0; i < p.count; i++) {
      const x = p.getX(i);
      const y = p.getY(i);
      const z = p.getZ(i);
      const key = `${x.toFixed(2)},${y.toFixed(2)},${z.toFixed(2)}`;
      if (!offsets.has(key)) {
        // The top rim stays on the footprint so the walkable edge is exact;
        // lower down the rock bulges out, widest at the waterline
        const depth = 1 - (y + 1.2) / (elev + 1.2);
        const bulge = y >= elev - 0.01 ? 0 : 0.5 + depth * 1.2 + rand() * 0.6;
        const nx = Math.abs(x) >= hx - 0.01 ? Math.sign(x) : 0;
        const nz = Math.abs(z) >= hz - 0.01 ? Math.sign(z) : 0;
        offsets.set(key, [nx * bulge + (rand() - 0.5) * 0.3 * (y < elev ? 1 : 0), y < elev - 0.01 && y > -1.1 ? (rand() - 0.5) * 0.4 : 0, nz * bulge + (rand() - 0.5) * 0.3 * (y < elev ? 1 : 0)]);
      }
      const [dx, dy, dz] = offsets.get(key);
      p.setXYZ(i, x + dx, y + dy, z + dz);
    }
    body.computeVertexNormals();
    // Colour faces: rock on the sides, darker and weedy at the waterline
    const colors = new Float32Array(p.count * 3);
    const c = new THREE.Color();
    for (let i = 0; i < p.count; i += 3) {
      const y = (p.getY(i) + p.getY(i + 1) + p.getY(i + 2)) / 3;
      c.set(y < 0.4 ? SEA.seaweed : y < elev * 0.5 ? SEA.rockDark : SEA.rock).offsetHSL(0, 0, (rand() - 0.5) * 0.06);
      for (let k = 0; k < 3; k++) colors.set([c.r, c.g, c.b], (i + k) * 3);
    }
    body.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    body.deleteAttribute('uv');

    const parts = [body];
    // Grass top with a sandy rim, exactly at the walking height
    parts.push(colored(new THREE.BoxGeometry(size.x - 0.6, 0.04, size.z - 0.6), SEA.grass, { y: elev + 0.02 }));
    for (const [w, d, x, z] of [[size.x, 0.6, 0, hz - 0.3], [size.x, 0.6, 0, -hz + 0.3], [0.6, size.z - 1.2, hx - 0.3, 0], [0.6, size.z - 1.2, -hx + 0.3, 0]]) {
      parts.push(colored(new THREE.BoxGeometry(w, 0.05, d), SEA.sandDark, { x, y: elev + 0.02, z }));
    }
    // Grass tufts and a few boulders round the edge, palms at the corners
    for (let i = 0; i < 18; i++) {
      const x = (rand() - 0.5) * (size.x - 2);
      const z = (rand() - 0.5) * (size.z - 2);
      parts.push(colored(new THREE.ConeGeometry(0.25, 0.5, 4), SEA.grassDark, { x, y: elev + 0.25, z, ry: rand() }));
    }
    for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
      parts.push(...palmParts(rand, { x: sx * (hx - 0.8), y: elev, z: sz * (hz - 0.8), ry: Math.atan2(sx, sz) + Math.PI / 2, height: 5 + rand() * 1.5, lean: 0.4 }));
      parts.push(rockGeometry(rand, 0.6, vary(SEA.rock, rand, 0.1), { x: sx * (hx + 0.6), y: 0.2, z: sz * (hz - 2), squash: 0.6 }));
    }
    this.addMerged(parts);
    this.mesh.add(foamMesh(foamFrame(size.x + 3.2, size.z + 3.2, 2.2)));
  }

  buildJetty(size, elevStart, elevEnd) {
    const rand = this.rand;
    const rise = elevEnd - elevStart;
    const slope = Math.atan2(rise, size.z);
    const slopeLength = Math.hypot(size.z, rise);
    const midY = (elevStart + elevEnd) / 2;
    const parts = [];

    // Stone causeway beneath the decking
    const body = this.createRampGeometry({ x: size.x - 0.6, z: size.z }, elevStart - 0.15, elevEnd - 0.15);
    const bodyColors = new Float32Array(body.attributes.position.count * 3);
    const stone = new THREE.Color(SEA.stoneDark);
    for (let i = 0; i < bodyColors.length; i += 3) bodyColors.set([stone.r, stone.g, stone.b], i);
    body.setAttribute('color', new THREE.BufferAttribute(bodyColors, 3));
    parts.push(body);

    // Plank decking following the slope, with a small gap between boards
    const boards = Math.max(4, Math.round(slopeLength / 0.55));
    const boardDepth = slopeLength / boards;
    for (let i = 0; i < boards; i++) {
      const t = (i + 0.5) / boards;
      parts.push(colored(new THREE.BoxGeometry(size.x - 0.3, 0.1, boardDepth - 0.06), vary(SEA.woodLight, rand, 0.14), {
        y: elevStart + t * rise - 0.05, z: -size.z / 2 + t * size.z, rx: -slope
      }));
    }
    // Pilings and rope rails along both sides
    const posts = Math.max(3, Math.round(size.z / 2.4));
    for (const side of [-1, 1]) {
      const x = side * (size.x / 2 - 0.15);
      let prev = null;
      for (let i = 0; i <= posts; i++) {
        const t = i / posts;
        const z = -size.z / 2 + t * size.z;
        const deck = elevStart + t * rise;
        const h = deck + 1.3;
        parts.push(colored(new THREE.CylinderGeometry(0.16, 0.2, h + 0.6, 6), vary(SEA.woodDark, rand, 0.1), { x, y: h / 2 - 0.3, z }));
        const top = new THREE.Vector3(x, deck + 1.1, z);
        if (prev) parts.push(...ropeParts(prev, top, { sag: 0.18, radius: 0.05, segments: 3 }));
        prev = top;
      }
      parts.push(colored(new THREE.BoxGeometry(0.2, 0.18, slopeLength), SEA.wood, { x, y: midY + 0.05, rx: -slope }));
    }
    this.addMerged(parts);
    this.mesh.add(foamMesh(foamFrame(size.x + 0.4, size.z, 1.0)));
  }

  createRampGeometry(size, elevStart, elevEnd) {
    // The ramp goes from elevStart at -Z to elevEnd at +Z
    const halfX = size.x / 2;
    const halfZ = size.z / 2;

    const vertices = new Float32Array([
      // Bottom face (y = 0)
      -halfX, 0, -halfZ,
       halfX, 0, -halfZ,
       halfX, 0,  halfZ,
      -halfX, 0,  halfZ,

      // Top face (sloped)
      -halfX, elevStart, -halfZ,
       halfX, elevStart, -halfZ,
       halfX, elevEnd,    halfZ,
      -halfX, elevEnd,    halfZ,
    ]);

    const indices = [
      // Bottom
      0, 1, 2,  0, 2, 3,
      // Top (sloped surface)
      4, 6, 5,  4, 7, 6,
      // Front (low end)
      0, 4, 5,  0, 5, 1,
      // Back (high end)
      2, 6, 7,  2, 7, 3,
      // Left side
      0, 3, 7,  0, 7, 4,
      // Right side
      1, 5, 6,  1, 6, 2,
    ];

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    geometry.setIndex(indices);
    const flat = geometry.toNonIndexed();
    flat.computeVertexNormals();
    return flat;
  }

  // Fade towards the given opacity (1 = solid) when blocking the camera's view.
  setFadeTarget(opacity) {
    if (this.fader) this.fader.target = opacity;
  }

  update(deltaTime, elapsed) {
    if (this.fader) this.fader.update(deltaTime);
    for (const floater of this.floaters) {
      const p = this.phase;
      floater.position.y = Math.sin(elapsed * 1.4 + p) * 0.06;
      floater.rotation.x = Math.sin(elapsed * 1.1 + p) * 0.02;
      floater.rotation.z = Math.cos(elapsed * 0.9 + p) * 0.02;
    }
    if (this.lamps.length) {
      LAMP_MATERIAL.color.set(SEA.lamp).multiplyScalar((elapsed % 2.5) < 0.4 ? 1.6 : 0.9);
    }
  }

  remove() {
    if (this.mesh) {
      this.scene.remove(this.mesh);
      this.mesh.traverse(child => {
        if (child.isMesh) child.geometry.dispose();
      });
      if (this.fader) this.fader.dispose();
      this.mesh = null;
    }
  }
}
